import { h, JSX } from 'preact'
import { FremmoteInfo, Geografi, ValgResultat } from '../../../../domain'
import { Swipe, SwipeItem } from '../../../shared/Swipe/Swipe'
import { InfoHeading } from '../shared'
import cx from 'classnames'
import { useCustomContext } from '../../../../../lib/context'
import { GA_EVENTS } from '../../../../../lib/analytics'
import { Stemmeprosent } from './StemmeProsent'
import { Forhandsstemmer } from './Forhandsstemmer'
import { BlankeStemmer } from './BlankeStemmer'
import { Lister } from './Lister'

import styles from './VissteDuAt.module.css'
import { useWidgetUrls } from '../../../../routes/widget'
import { idFrom } from '../../../../api'
import { Stemmeberettigede } from './Stemmeberettigede'

type Props = {
  geografi: Geografi
  resultat: ValgResultat
  valgtype: 'kommune' | 'fylke'
  widgetFooter?: JSX.Element
}

export function VissteDuAtInfo({ geografi, resultat, valgtype, widgetFooter }: Props) {
  const { partier = [] } = resultat
  const { sendGaEvent } = useCustomContext()
  const { vissteduatUrl } = useWidgetUrls()

  // TODO: Implementere ga-events på toppnivå (slik at de også virker i widget)
  // function handleActiveChange(activeIndex) {
  //   sendGaEvent(GA_EVENTS.VISSTE_DU_AT_ACTIVE_CHANGE(activeIndex))
  // }

  const blanke = resultat.partier && resultat.partier.find(parti => parti.parti.id === 'BLANKE')
  const blankeStemmer = blanke
    ? blanke.stemmer && blanke.stemmer.antall && blanke.stemmer.antall.total
    : null
  const ferdigOpptalt = !!resultat.opptalt && resultat.opptalt.forelopig >= 100
  const listeCount = partier.filter(parti => parti.parti.id !== 'BLANKE').length
  const frammoteProsent = getFremmoteProsent(valgtype, resultat.frammote)

  let elements
  if (geografi.type === 'Nasjon' || geografi.type === 'Fylke' || geografi.type === 'Kommune') {
    elements = landFylkeOgKommune(
      frammoteProsent,
      geografi,
      ferdigOpptalt,
      resultat,
      blankeStemmer,
      listeCount
    )
  } else {
    elements = bydelOgKrets(frammoteProsent, geografi, ferdigOpptalt)
  }

  if (elements.length === 0) {
    return null
  }

  return (
    <section
      data-widget-url={vissteduatUrl(geografi, valgtype)}
      data-valgtype={valgtype}
      data-grunndata={idFrom(geografi)}
      data-modulnavn="visste-du-at"
    >
      <InfoHeading id="visste-du-at-tittel"></InfoHeading>
      <Swipe
        class={styles.swipeWrapper}
        items={elements.map((elm, idx) => {
          const itemFirst = idx === 0
          const itemLast = idx === elements.length - 1
          return (
            <SwipeItem
              class={cx(styles.item, itemFirst && styles.itemFirst, itemLast && styles.itemLast)}
              data-active={itemFirst ? 'true' : 'false'}
            >
              {elm}
            </SwipeItem>
          )
        })}
      />
      {widgetFooter}
    </section>
  )
}

export function getFremmoteProsent(valgtype: 'kommune' | 'fylke', frammote?: FremmoteInfo) {
  /*
   * For fylkestingsvalg bruker vi frammote.prosent
   * For kommunestyrevalg, stortingsvalg og sametingsvalg bruker vi den største verdien av frammote.prosent og frammote.prosentStemmegiving
   * */

  if (!frammote) {
    return undefined
  }

  if (valgtype === 'fylke') {
    return frammote.prosent
  } else {
    if (frammote.prosent && frammote.prosentStemmegiving) {
      return Math.max(frammote.prosent, frammote.prosentStemmegiving)
    } else {
      return frammote.prosent || frammote.prosentStemmegiving
    }
  }
}

function landFylkeOgKommune(
  frammoteProsent,
  geografi,
  ferdigOpptalt,
  resultat,
  blankeStemmer,
  listeCount
) {
  return [
    !frammoteProsent && geografi.stemmeberettigede && (
      <Stemmeberettigede
        key="stemmeprosent"
        stemmeberettigede={geografi.stemmeberettigede}
        navn={geografi.navn}
      />
    ),
    frammoteProsent && geografi.stemmeberettigede && (
      <Stemmeprosent
        key="stemmeprosent"
        totalt={geografi.stemmeberettigede}
        navn={geografi.navn}
        frammoteProsent={frammoteProsent}
        ferdigOpptalt={ferdigOpptalt}
      />
    ),
    resultat.stemmer && resultat.stemmer.total > 0 && geografi.type !== 'Bydel' && (
      <Forhandsstemmer
        key="forhandsstemmer"
        ferdigOpptalt={ferdigOpptalt}
        antall={resultat.stemmer.fhs}
        totalt={resultat.stemmer.total}
        navn={geografi.navn}
      />
    ),
    typeof blankeStemmer === 'number' &&
      resultat &&
      resultat.stemmer &&
      resultat.stemmer.total !== undefined &&
      resultat.stemmer.total > 0 && (
        <BlankeStemmer
          key="blanke"
          ferdigOpptalt={ferdigOpptalt}
          antall={blankeStemmer}
          totalt={resultat.stemmer.total}
          navn={geografi.navn}
        />
      ),
    listeCount && geografi.type !== 'Bydel' && (
      <Lister
        key="lister"
        listeCount={listeCount}
        ferdigOpptalt={ferdigOpptalt}
        navn={geografi.navn}
      />
    )
  ].filter(Boolean) as JSX.Element[]
}

function bydelOgKrets(frammoteProsent, geografi, ferdigOpptalt) {
  if (geografi.navn.nb.toLowerCase().indexOf('uoppgitt') !== -1) {
    return []
  }

  return [
    !frammoteProsent && geografi.stemmeberettigede && (
      <Stemmeberettigede
        key="stemmeprosent"
        stemmeberettigede={geografi.stemmeberettigede}
        navn={geografi.navn}
        modifiers={{ krets: geografi.type === 'Krets' }}
      />
    ),
    frammoteProsent && geografi.stemmeberettigede && (
      <Stemmeprosent
        key="stemmeprosent"
        totalt={geografi.stemmeberettigede}
        navn={geografi.navn}
        frammoteProsent={frammoteProsent}
        ferdigOpptalt={ferdigOpptalt}
        modifiers={{ krets: geografi.type === 'Krets' }}
      />
    )
  ].filter(Boolean) as JSX.Element[]
}
